import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  Divider,
  Button,
  CircularProgress,
  Chip,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PlaceIcon from '@mui/icons-material/Place';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StarIcon from '@mui/icons-material/Star';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import usePriceOverview from '../../hooks/usePriceOverview';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const formatPrice = v => (typeof v === 'number' ? eur.format(v) : '—');

// Render a duration (stored in ms) as a compact German label.
const formatDuration = ms => {
  if (ms === null || ms === undefined) return '—';
  const totalMin = Math.round(ms / 60000);
  if (totalMin === 0) return '0 Min';
  if (totalMin % 60 === 0) return `${totalMin / 60} Std`;
  if (totalMin > 60) {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `${h} Std ${m} Min`;
  }
  return `${totalMin} Min`;
};

// Build the display rows/columns for a single price list by pivoting its price
// matrix into a category (rows) x duration (columns) grid. Declared categories
// and durations set the order; anything present only in the matrix is appended
// so no configured price is silently dropped.
function buildGrid(priceList) {
  const durationLabels = new Map();
  const bestsellerDurations = new Set();
  (priceList.durations || []).forEach(d => {
    durationLabels.set(d.durationMS, formatDuration(d.durationMS));
    if (d.isBestseller) bestsellerDurations.add(d.durationMS);
  });
  (priceList.matrix || []).forEach(m => {
    if (!durationLabels.has(m.durationMS)) durationLabels.set(m.durationMS, formatDuration(m.durationMS));
  });
  const durationCols = [...durationLabels.keys()].sort((a, b) => a - b);

  const catSeen = new Set();
  const categoryRows = [];
  (priceList.categories || [])
    .slice()
    .sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0))
    .forEach(c => {
      if (!catSeen.has(c.name)) {
        catSeen.add(c.name);
        categoryRows.push({ name: c.name, shortName: c.shortName, hint: c.hint });
      }
    });
  (priceList.matrix || []).forEach(m => {
    if (!catSeen.has(m.category)) {
      catSeen.add(m.category);
      categoryRows.push({ name: m.category, shortName: '', hint: '' });
    }
  });

  const cellIndex = new Map();
  (priceList.matrix || []).forEach(m => {
    cellIndex.set(`${m.category}::${m.durationMS}`, m);
  });

  return { durationCols, durationLabels, bestsellerDurations, categoryRows, cellIndex };
}

// Render the tiered prices (by minimum person count) of one matrix cell.
function PriceCell({ entry }) {
  if (!entry || !Array.isArray(entry.prices) || entry.prices.length === 0) {
    return <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>;
  }
  const tiers = entry.prices.slice().sort((a, b) => (a.minPersons || 0) - (b.minPersons || 0));
  if (tiers.length === 1) {
    return <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatPrice(tiers[0].price)}</Typography>;
  }
  return (
    <Stack spacing={0.25}>
      {tiers.map((t, i) => (
        <Typography key={i} variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
            ab {t.minPersons}:
          </Box>
          <Box component="span" sx={{ fontWeight: 700 }}>{formatPrice(t.price)}</Box>
        </Typography>
      ))}
    </Stack>
  );
}

function PriceListTable({ priceList }) {
  const { durationCols, durationLabels, bestsellerDurations, categoryRows, cellIndex } = useMemo(
    () => buildGrid(priceList),
    [priceList]
  );

  const hasGrid = durationCols.length > 0 && categoryRows.length > 0;

  return (
    <Box sx={{ mb: 2 }} className="price-list-block">
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" sx={{ mb: 0.75 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {priceList.list || priceList.title || 'Preisliste'}
        </Typography>
        {priceList.groupType && (
          <Chip
            size="small"
            color={priceList.groupType === 'kids' ? 'info' : 'default'}
            label={priceList.groupType === 'kids' ? 'Kinder' : priceList.groupType === 'adults' ? 'Erwachsene' : priceList.groupType}
          />
        )}
        {priceList.fsk && <Chip size="small" variant="outlined" label={`FSK ${priceList.fsk}`} />}
        {typeof priceList.offlinePaymentFee === 'number' && priceList.offlinePaymentFee > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={`Vor-Ort-Gebühr ${formatPrice(priceList.offlinePaymentFee)}`}
          />
        )}
      </Stack>

      {hasGrid ? (
        <Paper variant="outlined" sx={{ overflowX: 'auto', borderRadius: 1.5 }}>
          <Table size="small" className="price-table">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'action.hover', borderBottom: '2px solid', borderColor: 'divider' } }}>
                <TableCell sx={{ fontWeight: 700 }}>Kategorie</TableCell>
                {durationCols.map(d => (
                  <TableCell key={d} align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      {bestsellerDurations.has(d) && (
                        <StarIcon sx={{ fontSize: 15, color: 'warning.main' }} titleAccess="Bestseller" />
                      )}
                      <span>{durationLabels.get(d)}</span>
                    </Stack>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {categoryRows.map(cat => (
                <TableRow
                  key={cat.name}
                  sx={{ '&:nth-of-type(even)': { bgcolor: 'action.hover' }, '&:last-child td': { border: 0 } }}
                >
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{cat.name}</Typography>
                    {cat.hint && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {cat.hint}
                      </Typography>
                    )}
                  </TableCell>
                  {durationCols.map(d => (
                    <TableCell key={d} align="right" sx={{ verticalAlign: 'top' }}>
                      <PriceCell entry={cellIndex.get(`${cat.name}::${d}`)} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
          Keine Preise hinterlegt.
        </Typography>
      )}
    </Box>
  );
}

// Compact list of upgrade items (name → price) for one group.
function UpgradeGroup({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <Box sx={{ minWidth: 220, flex: '1 1 240px' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {title}
      </Typography>
      <Stack sx={{ mt: 0.5 }}>
        {items.map((it, i) => (
          <Stack
            key={it.upgradeId || i}
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{ py: 0.4, borderBottom: i < items.length - 1 ? '1px dashed' : 'none', borderColor: 'divider' }}
          >
            <Typography variant="body2">{it.name}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
              {formatPrice(it.price)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function ProductUpgrades({ upgrades }) {
  if (!upgrades) return null;
  const { packages = [], upsells = [], singleOffer = null } = upgrades;
  if (packages.length === 0 && upsells.length === 0 && !singleOffer) return null;
  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
      className="upgrades-block"
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
        <LocalOfferIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Zusatzoptionen
        </Typography>
      </Stack>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        <UpgradeGroup title="Pakete" items={packages} />
        <UpgradeGroup title="Upsells" items={upsells} />
        <UpgradeGroup title="Einzelangebot" items={singleOffer ? [singleOffer] : []} />
      </Stack>
    </Box>
  );
}

function ProductBlock({ product }) {
  return (
    <Box
      sx={{
        mb: 2.5,
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
      className="product-block"
    >
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {product.productName || product.internalName}
        </Typography>
        {product.internalName && product.internalName !== product.productName && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            ({product.internalName})
          </Typography>
        )}
      </Stack>
      {(product.priceLists || []).map(pl => (
        <PriceListTable key={pl.pricelistId} priceList={pl} />
      ))}
      <ProductUpgrades upgrades={product.upgrades} />
    </Box>
  );
}

function LocationBlock({ location }) {
  return (
    <Paper
      elevation={0}
      sx={{ mb: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
      className="location-block"
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ px: 2, py: 1.5, bgcolor: 'primary.main', color: 'primary.contrastText' }}
        className="location-header"
      >
        <PlaceIcon />
        <Typography variant="h5" sx={{ fontWeight: 800, flexGrow: 1 }}>
          {location.branchName}
        </Typography>
        <Chip
          size="small"
          label={`${location.products.length} Produkt(e)`}
          sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'inherit', fontWeight: 600 }}
        />
      </Stack>
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        {location.products.map(p => (
          <ProductBlock key={p.productId} product={p} />
        ))}
      </Box>
    </Paper>
  );
}

export default function PriceOverview() {
  const authCtx = useContext(AuthContext);
  const { getPriceOverview } = usePriceOverview();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [branchId, setBranchId] = useState('');
  const [search, setSearch] = useState('');

  // Full access OR own-assigned-branch access both grant viewing.
  const canRead =
    authCtx.checkRight('product:read') || authCtx.checkRight('product:read:priceOverviewOwn');

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await getPriceOverview();
      if (res) setData(res);
    } catch (err) {
      setLoadError(true);
      toast.error('Fehler beim Laden der Preisübersicht');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canRead) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply the branch filter and a case-insensitive product-name search to the
  // returned locations without mutating the source data.
  const filteredLocations = useMemo(() => {
    if (!data) return [];
    const term = search.trim().toLowerCase();
    return (data.locations || [])
      .filter(loc => !branchId || loc.branchId === branchId)
      .map(loc => {
        if (!term) return loc;
        const products = (loc.products || []).filter(p =>
          (p.productName || '').toLowerCase().includes(term) ||
          (p.internalName || '').toLowerCase().includes(term)
        );
        return { ...loc, products };
      })
      .filter(loc => loc.products.length > 0);
  }, [data, branchId, search]);

  if (!canRead) return <Navigate to="/welcome" replace />;

  const isScoped = data?.scope === 'assignedBranches';

  return (
    <Box sx={viewRootTextPrimarySx} className="price-overview-print">
      {/* Print rules: only the overview is printed, the app shell is hidden. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .price-overview-print, .price-overview-print * { visibility: visible !important; }
          .price-overview-print { position: absolute; top: 0; left: 0; width: 100%; padding: 0 !important; }
          .no-print { display: none !important; }
          .location-block { break-inside: avoid-page; box-shadow: none !important; border: 1px solid #ccc !important; }
          .location-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .price-list-block, .product-block, .upgrades-block { break-inside: avoid; }
          .price-table { font-size: 11px; }
          @page { margin: 12mm; }
        }
      `}</style>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
        className="no-print"
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Preisübersicht
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Read-only Übersicht aller Preislisten – gruppiert nach Standort
            {data?.generatedAt ? ` · Stand ${dayjs(data.generatedAt).format('DD.MM.YYYY HH:mm')}` : ''}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PictureAsPdfIcon />}
          onClick={() => window.print()}
          disabled={loading || !filteredLocations.length}
        >
          Als PDF exportieren
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 3 }} className="no-print">
        <TextField
          select
          label="Standort"
          value={branchId}
          onChange={e => setBranchId(e.target.value)}
          sx={{ minWidth: 240 }}
          size="small"
        >
          <MenuItem value="">Alle Standorte</MenuItem>
          {(data?.locations || []).map(loc => (
            <MenuItem key={loc.branchId} value={loc.branchId}>
              {loc.branchName}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Produkt suchen"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {isScoped && (
          <Chip size="small" color="info" variant="outlined" label="Nur zugewiesene Standorte" />
        )}
      </Stack>

      {/* Print-only header */}
      <Box sx={{ display: 'none', '@media print': { display: 'block', mb: 3 } }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Preisübersicht
        </Typography>
        {data?.generatedAt && (
          <Typography variant="body2">
            Stand {dayjs(data.generatedAt).format('DD.MM.YYYY HH:mm')}
          </Typography>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Alert severity="error" className="no-print">
          Die Preisübersicht konnte nicht geladen werden.
        </Alert>
      ) : !filteredLocations.length ? (
        <Alert severity="info" className="no-print">
          Keine Preislisten gefunden.
        </Alert>
      ) : (
        filteredLocations.map(loc => <LocationBlock key={loc.branchId} location={loc} />)
      )}
    </Box>
  );
}
